import React, { useEffect, useRef, useState } from "react";
import { IoChevronDown, IoClose } from "react-icons/io5";
import InputField from "components/fields/InputField";

const initialData = {
  name: "",
  email: "",
  phone: "",
  source: "",
  priority: "Medium",
};

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

const AddCareOfModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchSource, setSearchSource] = useState("");
  const [focusedSourceIndex, setFocusedSourceIndex] = useState(0);
  const dropdownRef = useRef();
  const sourceInputRef = useRef(null);

  useEffect(() => {
    if (openDropdown === "source" && sourceInputRef.current) {
      sourceInputRef.current.focus();
    }
  }, [openDropdown]);

  useEffect(() => {
    if (openDropdown === "source") {
      setFocusedSourceIndex(0);
    }
  }, [searchSource, openDropdown]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event) => {
      if (event.key === "Escape" && !loading) {
        // Removed to prevent closing on Escape key
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, loading, onClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialData);
      setErrors({});
      setOpenDropdown(null);
      setSearchSource("");
    }
  }, [isOpen]);

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const sourceOptions = ["Website", "Referral", "Ads", "Walk-In", "Other"];

  const toggleDropdown = (key, disabled = false) => {
    if (disabled) return;
    setOpenDropdown((prev) => (prev === key ? null : key));
    setSearchSource("");
  };

  const getFilteredSourceOptions = () => {
    const term = searchSource.trim().toLowerCase();
    if (!term) return sourceOptions;
    return sourceOptions.filter((option) => option.toLowerCase().includes(term));
  };

  const filteredSources = getFilteredSourceOptions();

  const selectSource = (option) => {
    setField("source", option);
    setOpenDropdown(null);
    setSearchSource("");
  };

  const handleSourceKeyDown = (event) => {
    if (openDropdown !== "source") return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedSourceIndex((prev) => Math.min(prev + 1, filteredSources.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedSourceIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredSources[focusedSourceIndex];
      if (option) selectSource(option);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpenDropdown(null);
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      nextErrors.name = "Name is required";
    } else if (!/^[A-Za-z ]+$/.test(trimmedName)) {
      nextErrors.name = "Name can only contain letters and spaces";
    }
    if (!formData.phone.trim()) nextErrors.phone = "Phone is required";
    else if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Phone must be a valid 10 digit number";
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Invalid email format";
    }
    if (!formData.source) nextErrors.source = "Source is required";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Extracts field errors and also returns 'from' errors for phone/email
  const getApiFieldErrors = (error) => {
    const apiErrors = error?.response?.data?.errors;
    if (!Array.isArray(apiErrors)) return {};
    const fieldErrors = {};
    let fromError = null;
    for (const item of apiErrors) {
      if (item?.path && item?.msg) {
        fieldErrors[item.path] = item.msg;
      }
      // Only one 'from' error for phone/email, prioritize phone
      if (
        item?.from === "controller" &&
        (item?.path === "phone" || item?.path === "email") &&
        !fromError
      ) {
        fromError = item.msg;
      }
    }
    if (fromError) fieldErrors._from = fromError;
    return fieldErrors;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        source: formData.source,
      };

      if (onSave) {
        await onSave(payload);
      }
      onClose();
    } catch (error) {
      const fieldErrors = getApiFieldErrors(error);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors, submit: "" }));
      } else {
        const apiMessage = error?.response?.data?.message || error?.message || "Failed to create care of";
        setErrors((prev) => ({ ...prev, submit: apiMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-md p-4">
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">Add New CareOf</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Provide minimal details to create a CareOf.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        {/* Show 'from' error for phone/email if present */}
        {errors._from && (
          <div className="mb-2 flex items-center justify-start">
            <span className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-400">
              {errors._from}
            </span>
          </div>
        )}

        {!errors._from && errors.submit && (
          <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-400">
            {errors.submit}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <InputField id="careof-name" label="Name*" placeholder="Enter name" value={formData.name} onChange={(e) => setField("name", e.target.value.replace(/[^A-Za-z ]/g, ""))} state={errors.name ? "error" : ""} />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>

          <div>
            <InputField id="careof-email" label="Email" type="email" placeholder="name@example.com" value={formData.email} onChange={(e) => setField("email", e.target.value)} state={errors.email ? "error" : ""} />
            {/* Only show error under input if not a 'from' error */}
            {errors.email && !errors._from && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
          </div>

          <div>
            <InputField id="careof-phone" label="Phone*" placeholder="10 digit mobile" value={formData.phone} onChange={(e) => setField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} state={errors.phone ? "error" : ""} />
            {/* Only show error under input if not a 'from' error */}
            {errors.phone && !errors._from && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
          </div>

          <div>
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">Source*</label>
            <div ref={dropdownRef} className="relative mt-2 w-full">
              <button
                type="button"
                onClick={() => toggleDropdown("source", false)}
                className={`flex h-12 w-full items-center justify-between rounded-xl border px-3 text-left text-sm ${errors.source
                  ? "border-red-500 text-red-500"
                  : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                  }`}
              >
                <span className="truncate">{formData.source || "Select source"}</span>
                <IoChevronDown className={`h-4 w-4 ${errors.source ? "text-red-500" : "text-gray-500"}`} />
              </button>

              {openDropdown === "source" && (
                <div className="absolute top-full z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                  <input
                    ref={sourceInputRef}
                    type="text"
                    value={searchSource}
                    onChange={(e) => setSearchSource(e.target.value)}
                    onKeyDown={handleSourceKeyDown}
                    placeholder="Search source"
                    className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setField("source", "");
                      setOpenDropdown(null);
                    }}
                    className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Clear
                  </button>
                  <div className="max-h-44 overflow-y-auto">
                    {filteredSources.length === 0 ? (
                      <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No options found</p>
                    ) : (
                      filteredSources.map((option, index) => (
                        <button
                          type="button"
                          key={option}
                          onClick={() => selectSource(option)}
                          onMouseEnter={() => setFocusedSourceIndex(index)}
                          className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${focusedSourceIndex === index
                            ? "bg-slate-100 text-navy-700 dark:bg-slate-700 dark:text-white"
                            : formData.source === option
                            ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-200"
                            }`}
                        >
                          {option}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {errors.source && <p className="mt-1 text-xs text-red-500">{errors.source}</p>}
          </div>

          {/* Priority Dropdown */}
          <div>
            <label className="text-sm font-bold text-navy-700 dark:text-white mb-2 block">
              Priority
            </label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:text-white"
            >
              {PRIORITY_OPTIONS.map((priority) => (
                <option key={priority} value={priority} className="dark:bg-gray-800">
                  {priority}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={loading} className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={loading} className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 sm:w-auto">{loading ? "Saving..." : "Add"}</button>
        </div>
      </div>
    </div>
  );
};

export default AddCareOfModal;
